import { useState } from "react";
import API_ROUTES from "./apiRoutes"; // ensure this has LOGIN defined
import { track } from "../lib/analytics";

/**
 * Login, agora em DUAS ETAPAS quando a conta tem segundo fator.
 *
 * Fluxo:
 *   1. `login()` manda email+senha. Se a conta NÃO tem 2FA, volta o token e acabou (igual
 *      a antes). Se TEM, volta `{ require2fa, challenge }` e NENHUM token.
 *   2. `confirmar2fa()` manda o desafio + o código do app (ou um código de backup) e aí sim
 *      recebe o token.
 *
 * ⚠️ Só gravamos no localStorage quando existe token DE VERDADE. Sem essa checagem,
 * `setItem("token", undefined)` gravaria a string "undefined", que depois iria no header
 * Authorization e daria 401 sem explicação nenhuma.
 */
export const useLogin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [desafio, setDesafio] = useState(null); // challenge do 2FA, quando exigido

  const guardar = (token) => {
    if (!token || typeof token !== "string") {
      throw new Error("Resposta inesperada do servidor. Tente de novo.");
    }
    localStorage.setItem("token", token);
    track("login_sucesso");
    setSuccess(true);
  };

  const login = async ({ email, senha }) => {
    setLoading(true);
    setError(null);

    if (!email && !senha) {
      setError("Preencha todos os campos.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(API_ROUTES.LOGIN.default(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password: senha }),
      });

      const resp = await response.json();

      if (!response.ok) {
        // 429 = rate limit (não é senha errada!). Mostra a mensagem real do servidor
        // pra pessoa não achar que errou a senha e sim que precisa aguardar.
        if (response.status === 429) {
          throw new Error(resp?.message || "Muitas tentativas. Aguarde alguns minutos e tente de novo.");
        }
        throw new Error("Email ou senha inválidos.");
      }

      // Conta com 2FA: a senha estava certa, mas o login ainda NÃO terminou.
      if (resp.require2fa) {
        setDesafio(resp.challenge);
        return;
      }

      guardar(resp.token);
    } catch (err) {
      setError(err.message || "Erro ao tentar fazer login.");
    } finally {
      setLoading(false);
    }
  };

  /** Segunda etapa: código do app autenticador OU um código de backup. */
  const confirmar2fa = async (codigo) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(API_ROUTES.LOGIN.twofa(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challenge: desafio, code: codigo }),
      });
      const resp = await response.json();
      if (!response.ok) {
        throw new Error(resp?.message || "Código inválido.");
      }
      guardar(resp.token);
    } catch (err) {
      setError(err.message || "Não foi possível confirmar o código.");
    } finally {
      setLoading(false);
    }
  };

  /** Volta pro passo da senha (ex.: a pessoa desistiu ou o desafio expirou). */
  const cancelar2fa = () => {
    setDesafio(null);
    setError(null);
  };

  return { login, confirmar2fa, cancelar2fa, desafio, success, loading, error };
};
